/**
 * Workspaces — Shared libraries with team seat invites.
 *
 * iter-78: role picker (admin/editor/viewer), pending email invites,
 * live seat usage vs. plan limit.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import { LayoutGrid, List, Zap, ZapOff } from "lucide-react";

const WS_VIEW_KEY = "bf360.workspaces.view";  // "grid" | "list"
const WS_SORT_KEY = "bf360.workspaces.sort";  // "recent" | "az" | "za" | "seats"

const ROLE_OPTIONS = [
  { value: "admin",  label: "Admin",  hint: "Full access — invite, delete, billing" },
  { value: "editor", label: "Editor", hint: "Read + edit all books" },
  { value: "viewer", label: "Viewer", hint: "Read-only across workspace" },
];

const ROLE_LABEL = { admin: "Admin", editor: "Editor", viewer: "Viewer" };

export default function Workspaces() {
  const { user, loading } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [name, setName] = useState("");
  const [inviteForm, setInviteForm] = useState({}); // { wsId: { email, role } }
  const [busy, setBusy] = useState(false);

  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(WS_VIEW_KEY) || "grid"; } catch { return "grid"; }
  });
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem(WS_SORT_KEY) || "recent"; } catch { return "recent"; }
  });
  useEffect(() => { try { localStorage.setItem(WS_VIEW_KEY, viewMode); } catch {} }, [viewMode]);
  useEffect(() => { try { localStorage.setItem(WS_SORT_KEY, sortMode); } catch {} }, [sortMode]);

  const sortedWorkspaces = useMemo(() => {
    const arr = [...(workspaces || [])];
    switch (sortMode) {
      case "az":     arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "za":     arr.sort((a, b) => (b.name || "").localeCompare(a.name || "")); break;
      case "seats":  arr.sort((a, b) => (b.seats?.used || 0) - (a.seats?.used || 0)); break;
      case "recent":
      default:       arr.sort((a, b) => ((b.created_at || "") > (a.created_at || "") ? 1 : -1));
    }
    return arr;
  }, [workspaces, sortMode]);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/workspaces/mine");
      setWorkspaces(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (user) reload(); }, [user, reload]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = (user.role || "").toLowerCase() === "admin";

  const setInviteField = (wsId, patch) => {
    setInviteForm((prev) => ({
      ...prev,
      [wsId]: { email: "", role: "editor", ...(prev[wsId] || {}), ...patch },
    }));
  };

  const createWorkspace = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Give the workspace a name");
    try {
      await api.post("/workspaces", { name: name.trim() });
      toast.success(`Created "${name.trim()}"`);
      setName("");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendInvite = async (wsId) => {
    const form = inviteForm[wsId] || {};
    const email = (form.email || "").trim().toLowerCase();
    const role = form.role || "editor";
    if (!email) return toast.error("Enter an email");
    try {
      const { data } = await api.post(`/workspaces/${wsId}/invites`, { email, role });
      if (data.status === "added_directly") {
        toast.success(`Added ${email} as ${ROLE_LABEL[role]}`);
      } else {
        toast.success(`Invite emailed to ${email}`);
      }
      setInviteForm((prev) => ({ ...prev, [wsId]: { email: "", role } }));
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const revokeInvite = async (wsId, inviteId, email) => {
    if (!window.confirm(`Revoke the invite to ${email}?`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/invites/${inviteId}`);
      toast.success("Invite revoked");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeMember = async (wsId, memberId, nickname) => {
    if (!window.confirm(`Remove ${nickname} from this workspace?`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/members/${memberId}`);
      toast.success("Member removed");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeRole = async (wsId, memberId, newRole) => {
    try {
      await api.patch(`/workspaces/${wsId}/members/${memberId}`, { role: newRole });
      toast.success(`Role updated to ${ROLE_LABEL[newRole]}`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-87 — Grant / revoke the workspace-owner-only "unlimited"
  // whitelist on a teammate. Optimistic UI + rollback.
  const toggleUnlimited = async (wsId, memberId, next, nickname) => {
    if (next && !window.confirm(`Grant ${nickname} unlimited renders (books + heroes)?`)) return;
    if (!next && !window.confirm(`Revoke ${nickname}'s unlimited access?`)) return;
    setWorkspaces((prev) => prev.map((w) => w.id !== wsId ? w : ({
      ...w,
      members: (w.members || []).map((m) => m.id === memberId
                                                 ? { ...m, is_unlimited: next } : m),
    })));
    try {
      await api.patch(`/workspaces/${wsId}/members/${memberId}/whitelist`, { unlimited: next });
      toast.success(next ? `${nickname} is now unlimited` : `${nickname}'s unlimited access revoked`);
    } catch (err) {
      setWorkspaces((prev) => prev.map((w) => w.id !== wsId ? w : ({
        ...w,
        members: (w.members || []).map((m) => m.id === memberId
                                                   ? { ...m, is_unlimited: !next } : m),
      })));
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="workspaces-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Library</p>
            <h1 className="font-editorial text-3xl mt-1">Shared workspaces</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-xl">
              Invite teammates by email — they get an Editor or Viewer seat. Every
              seat counts against your plan. Clients (paying customers you want to
              preview a book) live on the <Link to="/library/clients" className="link-underline">Clients page</Link>.
            </p>
          </div>
          <Link to="/dashboard" className="btn-ghost text-xs">← Dashboard</Link>
        </div>

        {isAdmin && (
          <form onSubmit={createWorkspace} className="border border-[color:var(--border)] bg-white p-4 mb-6 flex items-end gap-3" data-testid="ws-create-form">
            <label className="flex-1">
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">New workspace</span>
              <input value={name} onChange={(e)=>setName(e.target.value)}
                     placeholder='e.g. "The Book Studio"'
                     className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                     data-testid="ws-name-input"/>
            </label>
            <button type="submit" className="btn-primary" data-testid="ws-create-btn">Create</button>
          </form>
        )}

        {busy && workspaces.length === 0 && (
          <p className="text-sm text-[color:var(--text-muted)]">Loading…</p>
        )}
        {!busy && workspaces.length === 0 && (
          <p className="text-sm text-[color:var(--text-muted)] italic" data-testid="ws-empty">
            {isAdmin ? "No workspaces yet. Create one above."
                    : "You're not part of any shared workspace yet — ask an admin to add you."}
          </p>
        )}

        {workspaces.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4"
                data-testid="ws-toolbar">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">
              {sortedWorkspaces.length} workspace{sortedWorkspaces.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                Sort by
              </label>
              <select value={sortMode} onChange={(e)=>setSortMode(e.target.value)}
                      className="border border-[color:var(--border)] px-2 py-1 text-xs bg-white"
                      data-testid="ws-sort">
                <option value="recent">Recently created</option>
                <option value="az">Name A–Z</option>
                <option value="za">Name Z–A</option>
                <option value="seats">Seats used (most first)</option>
              </select>
              <div className="flex border border-[color:var(--border)]">
                <button onClick={()=>setViewMode("grid")}
                        className={`p-1.5 ${viewMode === "grid"
                                           ? "bg-[color:var(--text)] text-white"
                                           : "text-[color:var(--text-muted)] hover:bg-[color:var(--canvas)]"}`}
                        title="Card view"
                        data-testid="ws-view-grid"><LayoutGrid className="w-4 h-4" strokeWidth={1.5}/></button>
                <button onClick={()=>setViewMode("list")}
                        className={`p-1.5 ${viewMode === "list"
                                           ? "bg-[color:var(--text)] text-white"
                                           : "text-[color:var(--text-muted)] hover:bg-[color:var(--canvas)]"}`}
                        title="List view"
                        data-testid="ws-view-list"><List className="w-4 h-4" strokeWidth={1.5}/></button>
              </div>
            </div>
          </div>
        )}

        {viewMode === "list" && sortedWorkspaces.length > 0 && (
          <div className="border border-[color:var(--border)] bg-white overflow-x-auto"
                data-testid="ws-list-view">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border)]">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Members</th>
                  <th className="py-2 px-3">Seats</th>
                  <th className="py-2 px-3">My role</th>
                  <th className="py-2 px-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {sortedWorkspaces.map((w) => (
                  <tr key={w.id} className="border-b border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
                       data-testid={`ws-row-${w.id}`}>
                    <td className="py-2 px-3 font-medium">{w.name}</td>
                    <td className="py-2 px-3 text-[color:var(--text-muted)]">{(w.members || []).length}</td>
                    <td className="py-2 px-3 text-[color:var(--text-muted)]">
                      {w.seats?.used || 0} / {(w.seats?.limit || 1) === 9999 ? "∞" : (w.seats?.limit || 1)}
                    </td>
                    <td className="py-2 px-3 text-[color:var(--text-muted)] text-xs uppercase tracking-widest">
                      {w.my_role || "Editor"}
                    </td>
                    <td className="py-2 px-3 text-[color:var(--text-muted)] text-xs">
                      {(w.created_at || "").slice(0, 10) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === "grid" && (
        <div className="space-y-6">
          {sortedWorkspaces.map((w) => {
            const form = inviteForm[w.id] || { email: "", role: "editor" };
            const seats = w.seats || { used: 0, limit: 1 };
            const seatsFull = seats.used >= seats.limit;
            const iAmOwner = String(w.owner_user_id) === String(user.id);
            return (
              <section key={w.id} className="border border-[color:var(--border)] bg-white p-4"
                       data-testid={`ws-card-${w.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-lg font-semibold">{w.name}</h2>
                  <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]"
                          data-testid={`ws-${w.id}-seats`}>
                    {seats.used} / {seats.limit === 9999 ? "∞" : seats.limit} seat{seats.limit === 1 ? "" : "s"}
                  </span>
                </div>

                <ul className="mt-3 divide-y divide-[color:var(--border)]">
                  {w.members.map((m) => (
                    <li key={m.id} className="py-2 flex items-center justify-between text-sm gap-3"
                        data-testid={`ws-${w.id}-member-${m.id}`}>
                      <div className="min-w-0 flex-1">
                        {/* iter-91 — hide email addresses from teammates.
                            Owners keep the email tooltip on hover so they
                            can still confirm the seat matches an invite. */}
                        <span className="font-medium"
                              title={iAmOwner ? m.email : undefined}>
                          {(m.nickname || m.email || "").split(/[\s@]/)[0]}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.is_owner ? (
                          <span className="text-[10px] uppercase tracking-widest text-[color:var(--primary)]">Owner</span>
                        ) : iAmOwner ? (
                          <>
                            <select value={m.role || "editor"}
                                    onChange={(e)=>changeRole(w.id, m.id, e.target.value)}
                                    className="text-xs border border-[color:var(--border)] px-2 py-1 bg-white"
                                    data-testid={`ws-${w.id}-role-${m.id}`}>
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <button onClick={()=>toggleUnlimited(w.id, m.id, !m.is_unlimited, m.nickname)}
                                    className={"inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors " +
                                                (m.is_unlimited
                                                  ? "border-yellow-600 text-yellow-700 bg-yellow-50"
                                                  : "border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-yellow-700 hover:border-yellow-600")}
                                    title={m.is_unlimited
                                             ? "Revoke unlimited access"
                                             : "Grant unlimited renders (books + heroes)"}
                                    data-testid={`ws-${w.id}-whitelist-${m.id}`}>
                              {m.is_unlimited
                                ? <><Zap className="w-3 h-3" strokeWidth={2}/> Unlimited</>
                                : <><ZapOff className="w-3 h-3" strokeWidth={1.5}/> Grant</>}
                            </button>
                            <button onClick={()=>removeMember(w.id, m.id, m.nickname)}
                                    className="text-xs text-red-700 hover:underline"
                                    data-testid={`ws-${w.id}-remove-${m.id}`}>Remove</button>
                          </>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                            {ROLE_LABEL[m.role] || "Editor"}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Pending invites (owner-only) */}
                {iAmOwner && (w.pending_invites || []).length > 0 && (
                  <div className="mt-4 border-t border-[color:var(--border)] pt-3">
                    <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">
                      Pending invites ({w.pending_invites.length})
                    </p>
                    <ul className="space-y-1">
                      {w.pending_invites.map((inv) => (
                        <li key={inv.id} className="flex items-center justify-between text-sm py-1"
                             data-testid={`ws-${w.id}-invite-${inv.id}`}>
                          <span>
                            <span className="font-mono">{inv.email}</span>
                            <span className="ml-2 text-xs text-[color:var(--text-muted)]">
                              {ROLE_LABEL[inv.role] || "Editor"}
                            </span>
                          </span>
                          <button onClick={()=>revokeInvite(w.id, inv.id, inv.email)}
                                  className="text-xs text-red-700 hover:underline"
                                  data-testid={`ws-${w.id}-revoke-${inv.id}`}>Revoke</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {iAmOwner && (
                  <div className="mt-4 border-t border-[color:var(--border)] pt-4">
                    <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">
                      Invite a teammate
                    </p>
                    <div className="flex flex-col sm:flex-row items-end gap-2">
                      <label className="flex-1 w-full">
                        <span className="sr-only">Email</span>
                        <input value={form.email}
                               onChange={(e)=>setInviteField(w.id, { email: e.target.value })}
                               placeholder="alice@example.com"
                               className="w-full border border-[color:var(--border)] px-3 py-2 text-sm"
                               disabled={seatsFull}
                               data-testid={`ws-${w.id}-invite-email`}/>
                      </label>
                      <select value={form.role}
                              onChange={(e)=>setInviteField(w.id, { role: e.target.value })}
                              className="border border-[color:var(--border)] px-2 py-2 text-sm bg-white"
                              disabled={seatsFull}
                              data-testid={`ws-${w.id}-invite-role`}>
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <button onClick={()=>sendInvite(w.id)}
                              className="btn-primary text-sm"
                              disabled={seatsFull}
                              data-testid={`ws-${w.id}-invite-btn`}>
                        {seatsFull ? "Seats full" : "Send invite"}
                      </button>
                    </div>
                    <p className="text-[11px] text-[color:var(--text-muted)] mt-2">
                      {seatsFull
                        ? <span>All {seats.limit === 9999 ? "∞" : seats.limit} seats used. <Link to="/pricing" className="link-underline">Upgrade</Link> for more.</span>
                        : "Invitees receive an email with an accept link. Registered users are added instantly."}
                    </p>
                  </div>
                )}

                <p className="text-[11px] text-[color:var(--text-muted)] mt-4">
                  Workspace id: <span className="font-mono">{w.id}</span> — admins can move any book
                  into this workspace from the Dashboard card.
                </p>
              </section>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
